// src/pages/PayEdit.tsx (통째로 교체용 최종본)
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE, authFetch } from "../lib/auth";
import { hasBridge, isAppWebView, openExternalUrl } from "../lib/openExternal";

type PaymentStatus = "requested" | "approved" | "complete" | "failed" | "rejected";

type Payment = {
  id: number;
  title: string;
  amount: number;
  category?: string | null;
  status: PaymentStatus;
  created_at: string;
  extra_note?: string | null;
  group_id?: number | null;
  split_count?: number | null;
  split_index?: number | null;

  meta?: any;
  upload_session?: string | null;
  uploadSession?: string | null;
};

type SplitItem = {
  id: number;
  split_no: number;
  split_label: string;
  amount: number;
  fee_amount: number;
  vat_amount: number;
  total_amount: number;
  status?: string | null;
  approval_status?: string | null;
  created_at?: string | null;
};

type SplitSummary = {
  split_count: number;
  amount_sum: number;
  fee_sum: number;
  vat_sum: number;
  total_sum: number;
};

type UploadItem = {
  id: number;

  // ✅ 표시용(서버가 label로 내려줄 수 있음)
  doc_type?: string | null;

  // ✅ 키용(서버가 내려주면 이걸 우선 사용)
  doc_type_raw?: string | null;
  category_raw?: string | null;
  doc_type_label?: string | null;
  category_label?: string | null;

  filename?: string | null;
  saved_name?: string | null;
  original_name?: string | null;
  name?: string | null;

  created_at?: string | null;

  upload_session?: string | null;
  uploadSession?: string | null;

  payment_id?: number | null;
  paymentId?: number | null;

  preview_url?: string | null;
  download_url?: string | null;
};

function fmtAmount(n: number) {
  return n.toLocaleString("ko-KR", { maximumFractionDigits: 0 });
}

function parseAmountInput(v: string | number | null | undefined) {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

function calcPreviewAmounts(amount: number) {
  const a = Number(amount || 0);
  if (!Number.isFinite(a) || a <= 0) return { fee: 0, vat: 0, total: 0 };

  const fee = Math.round(a * 0.037);
  const vat = Math.round(fee * 0.1);
  return { fee, vat, total: a + fee + vat };
}

function normalizeSplitItemsPayload(items: any): SplitItem[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((x: any, idx: number) => ({
      id: Number(x?.id || 0),
      split_no: Number(x?.split_no || x?.splitNo || x?.split_index || idx + 1),
      split_label: String(x?.split_label || x?.splitLabel || `${idx + 1}/${items.length}`),
      amount: Number(x?.amount || 0),
      fee_amount: Number(x?.fee_amount || x?.feeAmount || 0),
      vat_amount: Number(x?.vat_amount || x?.vatAmount || 0),
      total_amount: Number(x?.total_amount || x?.totalAmount || 0),
      status: x?.status ?? null,
      approval_status: x?.approval_status ?? x?.approvalStatus ?? null,
      created_at: x?.created_at ?? x?.createdAt ?? null,
    }))
    .filter((x) => Number.isFinite(x.id) && x.id > 0);
}

function statusLabel(s: PaymentStatus) {
  switch (s) {
    case "requested":
      return "승인대기";
    case "approved":
      return "결제 가능";
    case "complete":
      return "결제 완료";
    case "failed":
    case "rejected":
      return "요청철회/거절";
    default:
      return s;
  }
}

function cleanText(v: any) {
  return String(v ?? "")
    .replace(/\r?\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function pickUploadSession(p?: Payment | null): string | null {
  if (!p) return null;
  const meta = p.meta || {};
  const v =
    p.upload_session ??
    p.uploadSession ??
    meta.upload_session ??
    meta.uploadSession ??
    meta.uploadSessionId ??
    meta.upload_session_id ??
    null;

  const s = cleanText(v);
  return s || null;
}

function normalizeUploadsPayload(j: any): UploadItem[] {
  const items = Array.isArray(j?.items) ? j.items : Array.isArray(j) ? j : [];
  return items
    .map((x: any) => ({
      id: Number(x?.id),

      doc_type: x?.doc_type ?? x?.docType ?? null,
      doc_type_raw: x?.doc_type_raw ?? x?.docTypeRaw ?? null,
      category_raw: x?.category_raw ?? x?.categoryRaw ?? null,
      doc_type_label: x?.doc_type_label ?? x?.docTypeLabel ?? null,
      category_label: x?.category_label ?? x?.categoryLabel ?? null,

      filename: x?.filename ?? x?.saved_name ?? x?.savedName ?? null,
      saved_name: x?.saved_name ?? x?.savedName ?? null,
      original_name: x?.original_name ?? x?.originalName ?? x?.name ?? null,
      name: x?.name ?? null,
      created_at: x?.created_at ?? x?.createdAt ?? null,
      upload_session: x?.upload_session ?? x?.uploadSession ?? null,
      uploadSession: x?.uploadSession ?? null,
      payment_id: x?.payment_id ?? x?.paymentId ?? null,
      paymentId: x?.paymentId ?? null,
      preview_url: x?.preview_url ?? x?.previewUrl ?? null,
      download_url: x?.download_url ?? x?.downloadUrl ?? null,
    }))
    .filter((x: any) => Number.isFinite(x.id) && x.id > 0);
}

function fileDisplayName(u: UploadItem) {
  return cleanText(u.original_name || u.name || u.saved_name || u.filename || "파일");
}

function fileKeyName(u: UploadItem) {
  // ✅ saved_name 우선
  return cleanText(u.saved_name || u.filename || "");
}

function docTypeKey(u: UploadItem) {
  // ✅ raw가 있으면 raw 우선 (서버 label 변환 대비)
  return cleanText(u.doc_type_raw || u.doc_type || "etc").toLowerCase() || "etc";
}

function docTypeLabel(u: UploadItem) {
  const raw = docTypeKey(u);
  const fallback: Record<string, string> = {
    quote: "견적서",
    counterparty_biz_reg: "상대방 사업자등록증",
    counterparty_bank_copy: "상대방 통장사본",
    counterparty_transfer_history: "기존 이체내역",
    counterparty_history: "기존 이체내역",
    rent_contract: "임대차계약서",
    mgmt_fee_rent: "관리비 영수증",
    rent_history: "기존 임차료 이체내역",
    mgmt_fee_maintenance: "관리비 영수증",
    payroll_statement: "급여 증빙",
    salary_transfer_history: "이체내역",
    estimate_or_statement: "물품대금 증빙",
    rental_contract: "렌탈 증빙",
    rental_payment_history: "이체내역",
    etc: "추가 서류",
  };

  const fromServer = cleanText(u.doc_type_label || "");
  if (fromServer && fromServer.toLowerCase() !== raw) return fromServer;
  return fallback[raw] || cleanText(u.doc_type) || "추가 서류";
}

type DocDef = { doc_type: string; title: string; required: number; hint?: string };

function inferCategorySlug(displayCategory?: string | null): string {
  const s = cleanText(displayCategory).toLowerCase();
  if (!s) return "unknown";

  // ✅ 코드/표시 혼용 대응
  if (s === "drink" || s.includes("주류") || s.includes("음료")) return "drink";
  if (s === "rent" || s.includes("임대") || s.includes("월세")) return "rent";
  if (s.includes("관리비")) return "mgmt_fee";
  if (s === "salary" || s.includes("급여")) return "salary";
  if (s === "goods" || s.includes("물품")) return "goods";
  if (s === "rental" || s.includes("렌탈")) return "rental";

  return "unknown";
}

function normalizeCategoryForUpload(value?: string | null): string | null {
  const slug = inferCategorySlug(value);
  return slug === "unknown" ? null : slug;
}

function docsForCategory(slug: string): {
  guideTitle: string;
  guideLines: string[];
  docs: DocDef[];
} {
  switch (slug) {
    case "drink":
      return {
        guideTitle: "주류 대금 필수 제출 서류",
        guideLines: [
          "• 견적서 1건 이상 제출이 필요합니다.",
          "• 상대방 사업자등록증 1건 이상 제출이 필요합니다.",
          "• 상대방 통장사본 1건 또는 기존 거래 이체내역 2건 이상 중 하나를 제출해 주세요.",
        ],
        docs: [
          { doc_type: "quote", title: "견적서", required: 1, hint: "거래처 발행 견적서 PDF 또는 이미지" },
          { doc_type: "counterparty_biz_reg", title: "상대방 사업자등록증", required: 1, hint: "공급사 사업자등록증 사본" },
          { doc_type: "counterparty_bank_copy", title: "상대방 통장사본", required: 1, hint: "입금받을 계좌 확인용 서류" },
          { doc_type: "counterparty_transfer_history", title: "기존 거래 이체내역", required: 2, hint: "통장사본이 없으면 2건 이상으로 대체" },
        ],
      };

    case "rent":
      return {
        guideTitle: "필수 제출 서류 안내",
        guideLines: [
          "• 임대차계약서 1건 제출 시 완료로 인정됩니다.",
          "• 계약서가 없을 경우: 관리비 영수증 + 기존 임차료 이체내역 2건 이상을 모두 제출해야 합니다.",
        ],
        docs: [
          { doc_type: "rent_contract", title: "임대차계약서", required: 1, hint: "PDF / 이미지(JPG, PNG) · 최대 20MB" },
          { doc_type: "mgmt_fee_rent", title: "관리비 영수증", required: 1, hint: "PDF / 이미지(JPG, PNG) · 최대 20MB" },
          { doc_type: "rent_history", title: "기존 임차료 이체내역", required: 2, hint: "PDF / 이미지(JPG, PNG) · 최대 20MB" },
        ],
      };

    case "mgmt_fee":
      return {
        guideTitle: "필수 제출 서류 안내",
        guideLines: ["• 관리비 영수증 2건 이상(필수)"],
        docs: [{ doc_type: "mgmt_fee_maintenance", title: "관리비 영수증", required: 2, hint: "PDF / 이미지(JPG, PNG) · 최대 20MB" }],
      };

    case "salary":
      return {
        guideTitle: "필수 제출 서류 안내",
        guideLines: ["• 급여 관련 증빙 1건 + 이체내역 1건(권장)"],
        docs: [
          { doc_type: "payroll_statement", title: "급여 증빙", required: 1, hint: "급여명세서/계약서 등" },
          { doc_type: "salary_transfer_history", title: "이체내역", required: 1, hint: "PDF / 이미지(JPG, PNG) · 최대 20MB" },
        ],
      };

    case "goods":
      return {
        guideTitle: "필수 제출 서류 안내",
        guideLines: ["• 물품대금 증빙 1건 + 이체내역 1건(권장)"],
        docs: [
          { doc_type: "estimate_or_statement", title: "물품대금 증빙", required: 1, hint: "계약서/세금계산서/견적서 등" },
          { doc_type: "counterparty_transfer_history", title: "이체내역", required: 1, hint: "PDF / 이미지(JPG, PNG) · 최대 20MB" },
        ],
      };

    case "rental":
      return {
        guideTitle: "필수 제출 서류 안내",
        guideLines: ["• 렌탈 계약/청구 증빙 1건 + 이체내역 1건(권장)"],
        docs: [
          { doc_type: "rental_contract", title: "렌탈 증빙", required: 1, hint: "계약서/청구서 등" },
          { doc_type: "rental_payment_history", title: "이체내역", required: 1, hint: "PDF / 이미지(JPG, PNG) · 최대 20MB" },
        ],
      };

    default:
      return {
        guideTitle: "첨부 서류",
        guideLines: ["• 필요한 파일을 첨부해 주세요."],
        docs: [],
      };
  }
}

function countByDocType(uploads: UploadItem[]) {
  const m = new Map<string, number>();
  for (const u of uploads) {
    const dt = docTypeKey(u);
    m.set(dt, (m.get(dt) || 0) + 1);
  }
  return m;
}

// ✅ multipart 전용 업로드: Content-Type을 절대 강제하지 않음
async function authFetchMultipart(url: string, fd: FormData) {
  const isAbs = /^https?:\/\//i.test(url);
  const base = (API_BASE || "").replace(/\/+$/, "");
  const path = url.replace(/^\/+/, "");
  const finalUrl = isAbs ? url : base ? `${base}/${path}` : `/${path}`;

  const token = localStorage.getItem("token") || "";
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(finalUrl, { method: "POST", headers, body: fd, credentials: "include" });
}

function pickFilenameFromDisposition(cd: string | null): string | null {
  if (!cd) return null;
  // content-disposition: attachment; filename="abc.pdf"
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(cd);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

function withAuthToken(path: string): string {
  const token = localStorage.getItem("token") || "";
  const base = (API_BASE || "").replace(/\/+$/, "");
  const sep = path.includes("?") ? "&" : "?";
  return token
    ? `${base}${path}${sep}token=${encodeURIComponent(token)}`
    : `${base}${path}`;
}

function isMobileUa() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod/i.test(ua);
}

function openDirectForWebView(path: string) {
  const url = withAuthToken(path);
  if (hasBridge()) {
    openExternalUrl(url);
    return;
  }

  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) window.location.assign(url);
}

function PayEditUploadCard(props: {
  title: string;
  desc: string;
  needCount: number;
  okCount: number;
  files: UploadItem[];
  docType: string;
  busy?: boolean;
  disabled?: boolean;
  onUpload: (docType: string, files: FileList | null) => Promise<void>;
  onPreview: (u: UploadItem) => Promise<void>;
  onDelete: (uploadId: number) => Promise<void>;
}) {
  const { title, desc, needCount, okCount, files, docType, busy, disabled, onUpload, onPreview, onDelete } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const met = okCount >= needCount;
  const remain = Math.max(needCount - okCount, 0);
  const statusText = met ? "충족" : `미충족${remain > 0 ? ` · ${remain}건 부족` : ""}`;
  const statusClass = met
    ? "text-xs font-extrabold text-[#c2410c]"
    : "text-xs font-extrabold text-orange-600";
  const uploadBtnClass = [
    "mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-3 py-2 text-xs font-extrabold text-white shadow-sm active:translate-y-px focus:outline-none focus:ring-2 disabled:opacity-60",
    met
      ? "bg-[#f97316] hover:bg-[#f97316] focus:ring-[#f97316]/30"
      : "bg-rose-500 hover:bg-rose-600 focus:ring-rose-500/30",
  ].join(" ");

  const openPicker = () => {
    const input = inputRef.current;
    if (!input || busy || disabled) return;

    if (isAppWebView()) {
      const useCamera = window.confirm("카메라로 촬영할까요?\n확인: 카메라  /  취소: 파일 선택");
      if (useCamera) {
        input.accept = "image/*";
        input.multiple = false;
        input.setAttribute("capture", "environment");
      } else {
        input.accept = ".pdf,image/*";
        input.multiple = true;
        input.removeAttribute("capture");
      }
    }

    input.click();
  };

  return (
    <div
      className={[
        "rounded-2xl border bg-white p-4 shadow-sm",
        met ? "border-orange-100" : "border-orange-300 bg-orange-50/40 ring-1 ring-orange-200/70",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        <h3 className="font-extrabold text-slate-900">{title}</h3>
        <span className={statusClass}>{statusText}</span>
        <span className="ml-auto text-xs text-slate-500">필요: {needCount}건</span>
      </div>

      <div className="mt-1 text-xs text-slate-600">{desc}</div>

      {files.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-xl bg-orange-50 p-2">
          {files.map((f) => (
            <li
              key={`${f.id ?? "x"}-${f.saved_name ?? "y"}-${f.original_name ?? "z"}`}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 text-[11px] text-slate-700"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-700">
                    {docTypeLabel(f)}
                  </span>
                  <span className="truncate text-left">{fileDisplayName(f)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 whitespace-nowrap justify-end">
                <button
                  type="button"
                  className="text-[11px] font-semibold text-orange-700 hover:underline"
                  onClick={() => onPreview(f)}
                >
                  미리보기
                </button>
                <button
                  type="button"
                  className="text-[11px] font-semibold text-rose-600 hover:underline"
                  onClick={() => onDelete(f.id)}
                  disabled={disabled}
                >
                  삭제
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = e.currentTarget.files;
          onUpload(docType, files).catch(() => void 0);
          e.currentTarget.value = "";
          e.currentTarget.accept = ".pdf,image/*";
          e.currentTarget.multiple = true;
          e.currentTarget.removeAttribute("capture");
        }}
      />

      <button type="button" className={uploadBtnClass} onClick={openPicker} disabled={busy || disabled}>
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor">
          <path
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 12.5V7a5 5 0 0 0-10 0v9a3 3 0 0 0 6 0V8.5"
          />
          <path
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M11 12v4a5 5 0 0 1-10 0v-5a7 7 0 1 1 14 0v6"
          />
        </svg>
        {busy ? "처리 중…" : "파일 업로드"}
      </button>
    </div>
  );
}

export default function PayEdit() {
  const { id } = useParams();
  const paymentId = Number(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [row, setRow] = useState<Payment | null>(null);

  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [splitItems, setSplitItems] = useState<SplitItem[]>([]);
  const [splitAmounts, setSplitAmounts] = useState<string[]>([]);

  const categoryText = category === "drink" ? "주류/음료" : (category || "");

  const [uploadSession, setUploadSession] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [uploadsError, setUploadsError] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [showAllMyUploads, setShowAllMyUploads] = useState(false);

  const editable = useMemo(() => {
    if (!row) return false;
    return row.status === "requested" || row.status === "failed" || row.status === "rejected";
  }, [row]);

  const canCancel = useMemo(() => row?.status === "requested", [row]);
  const isRejectedLike = useMemo(() => row?.status === "failed" || row?.status === "rejected", [row?.status]);

  const catSlug = useMemo(() => inferCategorySlug(category || row?.category || ""), [category, row?.category]);
  const docPack = useMemo(() => docsForCategory(catSlug), [catSlug]);

  const allowedDocTypes = useMemo(() => {
    const s = new Set<string>();
    for (const d of docPack.docs) s.add(cleanText(d.doc_type));
    s.add("etc");
    return s;
  }, [docPack.docs]);

  const visibleUploads = useMemo(() => {
    if (docPack.docs.length === 0) return uploads;
    if (showAllMyUploads) return uploads;
    return uploads.filter((u) => allowedDocTypes.has(docTypeKey(u)));
  }, [uploads, showAllMyUploads, allowedDocTypes, docPack.docs.length]);

  const docCountMap = useMemo(() => countByDocType(uploads), [uploads]);
  const isSplitPayment = splitItems.length > 1;
  const totalAmountNumber = useMemo(() => parseAmountInput(amount), [amount]);
  const splitAmountNumbers = useMemo(
    () => splitAmounts.map((value) => parseAmountInput(value)),
    [splitAmounts]
  );
  const originalSplitSummary = useMemo<SplitSummary>(() => {
    if (!splitItems.length) {
      return { split_count: 0, amount_sum: 0, fee_sum: 0, vat_sum: 0, total_sum: 0 };
    }
    return splitItems.reduce(
      (acc, item) => {
        acc.split_count += 1;
        acc.amount_sum += Number(item.amount || 0);
        acc.fee_sum += Number(item.fee_amount || 0);
        acc.vat_sum += Number(item.vat_amount || 0);
        acc.total_sum += Number(item.total_amount || 0);
        return acc;
      },
      { split_count: 0, amount_sum: 0, fee_sum: 0, vat_sum: 0, total_sum: 0 }
    );
  }, [splitItems]);
  const splitDraftSummary = useMemo<SplitSummary>(() => {
    if (!splitAmountNumbers.length) {
      return { split_count: 0, amount_sum: 0, fee_sum: 0, vat_sum: 0, total_sum: 0 };
    }
    return splitAmountNumbers.reduce(
      (acc, value) => {
        const safeValue = Number.isFinite(value) && value > 0 ? value : 0;
        const preview = calcPreviewAmounts(safeValue);
        acc.split_count += 1;
        acc.amount_sum += safeValue;
        acc.fee_sum += preview.fee;
        acc.vat_sum += preview.vat;
        acc.total_sum += preview.total;
        return acc;
      },
      { split_count: 0, amount_sum: 0, fee_sum: 0, vat_sum: 0, total_sum: 0 }
    );
  }, [splitAmountNumbers]);
  const splitAmountsValid = useMemo(
    () =>
      !isSplitPayment ||
      (splitAmounts.length === splitItems.length &&
        splitAmountNumbers.every((value) => Number.isFinite(value) && value > 0)),
    [isSplitPayment, splitAmountNumbers, splitAmounts.length, splitItems.length]
  );
  const splitSumMatchesTotal = useMemo(
    () => !isSplitPayment || splitDraftSummary.amount_sum === totalAmountNumber,
    [isSplitPayment, splitDraftSummary.amount_sum, totalAmountNumber]
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!Number.isFinite(paymentId) || paymentId <= 0) {
        setError("잘못된 결제 ID 입니다.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await authFetch(`/api/payments/${paymentId}`, { cache: "no-store" as RequestCache });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || !j?.ok) throw new Error(j?.message || "결제 정보를 불러오지 못했습니다.");

        const p: Payment = j.row || j.payment || j.item || j.data || j;
        if (cancelled) return;

        const loadedSplitItems = normalizeSplitItemsPayload(j?.split_items ?? p?.split_items ?? []);
        const loadedTotalAmount = loadedSplitItems.length > 1
          ? loadedSplitItems.reduce((sum, item) => sum + Number(item.amount || 0), 0)
          : Number(p.amount ?? 0);

        setRow(p);
        setTitle(p.title || "");
        setAmount(String(loadedTotalAmount || p.amount || ""));
        setCategory(normalizeCategoryForUpload(p.category) || String(p.category || ""));
        setSplitItems(loadedSplitItems);
        setSplitAmounts(loadedSplitItems.map((item) => String(item.amount || "")));

        const sess = pickUploadSession(p);
        setUploadSession(sess);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "로드 실패");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  const loadUploads = async (sess?: string | null) => {
    const session = sess ?? uploadSession;
    if (!session) {
      setUploads([]);
      return;
    }

    setUploadsLoading(true);
    setUploadsError(null);

    try {
      // ✅ payment_id 필터는 서버 구현/데이터 상태에 따라 비어버릴 수 있어 제외하고 session_key만 사용
      const res = await authFetch(`/api/uploads/my?session_key=${encodeURIComponent(session)}`, {
        cache: "no-store" as RequestCache,
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.message || "첨부파일을 불러오지 못했습니다.");

      const list = normalizeUploadsPayload(j);

      // 서버가 upload_session을 내려주면 안전하게 한 번 더 필터(없으면 그대로)
      const filtered = list.filter((x) => {
        const us = cleanText(x.upload_session ?? x.uploadSession ?? "");
        return !us || us === session;
      });

      setUploads(filtered);
    } catch (e: any) {
      console.error("[PayEdit] loadUploads error", e);
      setUploads([]);
      setUploadsError(e?.message || "첨부파일을 불러오지 못했습니다.");
    } finally {
      setUploadsLoading(false);
    }
  };

  useEffect(() => {
    if (!row) return;
    const sess = pickUploadSession(row);
    if (sess) loadUploads(sess).catch(() => void 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id]);

  const uploadFiles = async (docType: string, files: FileList | null) => {
    if (!editable) {
      alert("현재 상태에서는 첨부서류를 수정할 수 없습니다.");
      return;
    }
    const session = uploadSession;
    if (!session) {
      alert("이 결제건에 upload_session(첨부 묶음키)이 없습니다. 서버에서 결제건에 upload_session 저장이 필요합니다.");
      return;
    }
    if (!files || files.length === 0) return;

    setUploadingDoc(docType);
    setUploadsError(null);

    try {
      const fd = new FormData();

      // ✅ 서버가 어떤 키를 받아도 되게 둘 다 넣음
      fd.append("session_key", session);
      fd.append("upload_session", session);

      fd.append("doc_type", docType);
      fd.append("payment_id", String(paymentId));
      fd.append(
        "category",
        normalizeCategoryForUpload(category || row?.category || "drink") || "drink"
      );

      Array.from(files).forEach((f) => fd.append("files", f));

      // ✅ 여기서 authFetch 쓰지 말고 multipart 전용 fetch 사용
      const res = await authFetchMultipart("/api/uploads", fd);
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.message || "파일 업로드에 실패했습니다.");

      await loadUploads(session);
    } catch (e: any) {
      console.error("[PayEdit] upload error", e);
      setUploadsError(e?.message || "업로드 중 오류가 발생했습니다.");
    } finally {
      setUploadingDoc(null);
    }
  };

  const openNativePicker = (docType: string) => {
    if (!editable) return alert("현재 상태에서는 첨부서류를 수정할 수 없습니다.");
    if (saving || uploadingDoc) return;
    if (sessionMissing) {
      alert("이 결제건에 upload_session(첨부 묶음키)이 없습니다. 서버에서 결제건에 upload_session 저장이 필요합니다.");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,image/*";
    input.style.position = "fixed";
    input.style.left = "-9999px";
    input.style.top = "-9999px";

    if (isAppWebView()) {
      const useCamera = window.confirm("카메라로 촬영할까요?\n확인: 카메라  /  취소: 파일 선택");
      if (useCamera) {
        input.accept = "image/*";
        input.multiple = false;
        input.setAttribute("capture", "environment");
      }
    }

    input.addEventListener(
      "change",
      () => {
        const files = input.files;
        uploadFiles(docType, files).catch(() => void 0);
        input.remove();
      },
      { once: true }
    );

    document.body.appendChild(input);
    input.click();
  };


  const deleteUpload = async (uploadId: number) => {
    if (!editable) {
      alert("현재 상태에서는 첨부서류를 수정할 수 없습니다.");
      return;
    }

    const target = uploads.find((x) => x.id === uploadId);
    const name = target ? fileDisplayName(target) : String(uploadId);

    const ok = window.confirm(`이 첨부파일을 삭제하시겠습니까?\n\n- 파일: ${name}`);
    if (!ok) return;

    setDeletingId(uploadId);
    setUploadsError(null);

    try {
      let res = await authFetch(`/api/uploads/${uploadId}`, { method: "DELETE" });
      let j: any = await res.json().catch(() => ({}));

      if (!res.ok || !j?.ok) {
        res = await authFetch(`/api/uploads/${uploadId}/delete`, { method: "POST" });
        j = await res.json().catch(() => ({}));
      }

      if (!res.ok || !j?.ok) throw new Error(j?.message || "삭제에 실패했습니다.");
      await loadUploads(uploadSession);
    } catch (e: any) {
      console.error("[PayEdit] deleteUpload error", e);
      setUploadsError(e?.message || "삭제 중 오류가 발생했습니다.");
    } finally {
      setDeletingId(null);
    }
  };

  // ✅ 미리보기: ID 기반 엔드포인트(/preview-id/:id)를 우선 사용 (nginx 정적 규칙 회피용)
  const openPreview = async (u: UploadItem) => {
    try {
      if (isAppWebView() || hasBridge() || isMobileUa()) {
        openDirectForWebView(`/api/uploads/preview-id/${u.id}`);
        return;
      }

      // 1) ID 기반 (추천)
      let res = await authFetch(`/api/uploads/preview-id/${u.id}`, { cache: "no-store" as RequestCache });
      if (!res.ok) {
        // 2) 폴백(기존): filename 기반
        const fn = fileKeyName(u);
        if (fn) {
          res = await authFetch(`/api/uploads/preview/${encodeURIComponent(fn)}?debug=1`, {
            cache: "no-store" as RequestCache,
          });
        }
      }

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `미리보기 실패 (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      openExternalUrl(url);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      alert(e?.message || "미리보기 중 오류가 발생했습니다.");
    }
  };

  // ✅ 다운로드: ID 기반(/download-id/:id) + blob 저장
  const openDownload = async (u: UploadItem) => {
    try {
      if (isAppWebView() || hasBridge() || isMobileUa()) {
        openDirectForWebView(`/api/uploads/download-id/${u.id}`);
        return;
      }

      let res = await authFetch(`/api/uploads/download-id/${u.id}`, { cache: "no-store" as RequestCache });
      if (!res.ok) {
        const fn = fileKeyName(u);
        if (fn) {
          res = await authFetch(`/api/uploads/download/${encodeURIComponent(fn)}?debug=1`, {
            cache: "no-store" as RequestCache,
          });
        }
      }

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `다운로드 실패 (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      const cd = res.headers.get("content-disposition");
      const fromHeader = pickFilenameFromDisposition(cd);
      const name = fromHeader || fileDisplayName(u) || "download";

      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      alert(e?.message || "다운로드 중 오류가 발생했습니다.");
    }
  };

  const updateSplitAmount = (index: number, value: string) => {
    setSplitAmounts((current) => current.map((item, idx) => (idx === index ? value : item)));
  };

  const onSave = async () => {
    if (!row) return;
    if (!editable) return alert("수정 불가 상태입니다.");

    const amt = parseAmountInput(amount);
    if (!Number.isFinite(amt) || amt <= 0) return alert("금액은 0보다 큰 숫자여야 합니다.");

    if (isSplitPayment) {
      if (!splitAmountsValid) {
        return alert("분할 금액은 모두 0보다 큰 숫자로 입력해 주세요.");
      }
      if (!splitSumMatchesTotal) {
        return alert("분할 합계와 총 요청금액이 정확히 일치해야 저장할 수 있습니다.");
      }
    }

    setSaving(true);
    setError(null);

    try {
      const payload: Record<string, any> = {
        title: title.trim() || row.title,
        amount: amt,
        category: category.trim() || null,
      };

      if (isSplitPayment) {
        payload.total_principal = amt;
        payload.split_amounts = splitAmountNumbers;
      }

      const res = await authFetch(`/api/payments/${paymentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.message || "저장에 실패했습니다.");

      if (isRejectedLike) {
        alert("수정 후 재접수되었습니다. 관리자 검토 후 다시 안내됩니다.");
        navigate("/pay");
        return;
      }

      alert("수정되었습니다.");
      navigate("/pay");
    } catch (e: any) {
      console.error("[PayEdit] save error", e);
      setError(e?.message || "저장 중 오류");
    } finally {
      setSaving(false);
    }
  };

  const onCancelRequest = async () => {
    if (!row) return;
    if (canCancel !== true) return alert("요청철회가 불가능한 상태입니다.");

    const ok = window.confirm("이 결제요청을 철회하시겠습니까?\n\n철회 후에는 관리자에게 진행 대상으로 노출되지 않습니다.");
    if (!ok) return;

    setSaving(true);
    setError(null);

    try {
      const res = await authFetch(`/api/payments/${paymentId}/cancel`, { method: "POST" });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.ok) throw new Error(j?.message || "요청철회에 실패했습니다.");

      alert("결제요청을 철회했습니다.");
      navigate("/pay");
    } catch (e: any) {
      console.error("[PayEdit] cancel error", e);
      setError(e?.message || "요청철회 중 오류");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-slate-50 pb-20">
        <div className="mx-auto max-w-3xl px-4 pt-6">
          <div className="rounded-2xl bg-white p-6 text-sm text-slate-500 shadow-sm ring-1 ring-slate-200/70">
            불러오는 중입니다...
          </div>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="min-h-[100dvh] bg-slate-50 pb-20">
        <div className="mx-auto max-w-3xl px-4 pt-6">
          <div className="rounded-2xl bg-white p-6 text-sm text-rose-700 shadow-sm ring-1 ring-rose-200/70">
            {error || "결제 정보를 찾을 수 없습니다."}
          </div>
          <div className="mt-4">
            <button
              className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white"
              onClick={() => navigate("/pay")}
            >
              목록으로
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sessionMissing = !uploadSession;

  return (
    <div className="min-h-[100dvh] bg-slate-50 pb-20">
      <div className="mx-auto max-w-3xl px-4">
        <div className="pt-2 sm:pt-3">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">결제요청 수정</h1>
          <p className="mt-1 text-sm text-slate-500">
            승인대기 또는 요청철회/거절 상태에서만 수정할 수 있습니다. (거절건은 "저장 후 재접수"로 다시 검토 요청됩니다)
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {/* 첨부서류 */}
        <div className="mt-5 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
          <div className="text-lg font-bold tracking-tight text-slate-900">첨부서류</div>
          <div className="mt-1 text-sm text-slate-500">보완해야 할 자료를 삭제/추가 업로드할 수 있습니다.</div>

          {sessionMissing && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              이 결제건은 <b>upload_session(첨부 묶음키)</b> 정보가 없어, "해당 결제건 첨부만" 정확히 연결할 수 없습니다.
              <br />
              서버에서 결제건(meta 또는 컬럼)에 upload_session을 저장하도록 처리되면 정상적으로 분리됩니다.
            </div>
          )}

          <div className="mt-4 rounded-2xl bg-slate-50 p-4">
            <div className="text-sm font-semibold text-[#c2410c]">{docPack.guideTitle}</div>
            <ul className="mt-2 space-y-1 text-xs text-slate-700">
              {docPack.guideLines.map((ln, i) => (
                <li key={i}>{ln}</li>
              ))}
              <li className="pt-1 text-[11px] text-slate-500">※ 추가 서류 요청이 있을 수 있습니다.</li>
            </ul>

            {docPack.docs.length > 0 && (
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {docPack.docs.map((d) => {
                  const key = cleanText(d.doc_type).toLowerCase();
                  const cnt = docCountMap.get(cleanText(d.doc_type)) || 0;
                  const cardFiles = uploads.filter((u) => docTypeKey(u) === key);

                  return (
                    <PayEditUploadCard
                      key={d.doc_type}
                      title={d.title}
                      desc={d.hint || "PDF / 이미지(JPG, PNG) · 최대 20MB"}
                      needCount={d.required}
                      okCount={cnt}
                      files={cardFiles}
                      docType={d.doc_type}
                      busy={uploadingDoc === d.doc_type}
                      disabled={!editable || saving || sessionMissing}
                      onUpload={uploadFiles}
                      onPreview={openPreview}
                      onDelete={deleteUpload}
                    />
                  );
                })}
              </div>
            )}

            {uploadsError && (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {uploadsError}
              </div>
            )}

            <div className="mt-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-semibold text-slate-900">첨부파일 목록</div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={showAllMyUploads}
                      onChange={(e) => setShowAllMyUploads(e.target.checked)}
                      className="h-4 w-4"
                    />
                    모든 첨부 보기
                  </label>

                  <button
                    type="button"
                    onClick={() => loadUploads(uploadSession).catch(() => void 0)}
                    disabled={uploadsLoading || sessionMissing}
                    className={[
                      "rounded-full px-4 py-2 text-xs font-semibold",
                      uploadsLoading || sessionMissing
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                        : "bg-slate-900 text-white hover:brightness-110",
                    ].join(" ")}
                  >
                    {uploadsLoading ? "불러오는 중..." : "새로고침"}
                  </button>
                </div>
              </div>

              {uploadsLoading && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
                  첨부파일을 불러오는 중입니다...
                </div>
              )}

              {!uploadsLoading && visibleUploads.length === 0 && (
                <div className="mt-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
                  첨부된 파일이 없습니다.
                </div>
              )}

              {!uploadsLoading && visibleUploads.length > 0 && (
                <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <ul className="divide-y divide-slate-100">
                    {visibleUploads.map((u) => {
                      const dtKey = docTypeKey(u);
                      const dtLabel = docTypeLabel(u);
                      const name = fileDisplayName(u);
                      const deleting = deletingId === u.id;

                      return (
                        <li
                          key={u.id}
                          className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                                {dtLabel}
                              </span>
                              <span className="truncate text-sm font-medium text-slate-900" title={name}>
                                {name}
                              </span>
                            </div>
                            {u.created_at && (
                              <div className="mt-0.5 text-[11px] text-slate-500">
                                업로드: {cleanText(u.created_at)}
                              </div>
                            )}
                          </div>

                          <div className="flex shrink-0 flex-wrap justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openPreview(u).catch(() => void 0)}
                              className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                            >
                              미리보기
                            </button>

                            <button
                              type="button"
                              onClick={() => deleteUpload(u.id).catch(() => void 0)}
                              disabled={!editable || saving || deleting}
                              className={[
                                "rounded-full px-4 py-2 text-xs font-semibold",
                                editable && !saving && !deleting
                                  ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100"
                                  : "bg-slate-200 text-slate-500 cursor-not-allowed",
                              ].join(" ")}
                            >
                              {deleting ? "삭제 중..." : "삭제"}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 기본 정보 수정 */}
        <div className="mt-5 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70">
          <div className="text-sm text-slate-500">
            현재 상태: <span className="font-semibold text-slate-900">{statusLabel(row.status)}</span>
          </div>

          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-800">제목</label>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600"
                value={title}
                placeholder="예) 관리비"
                readOnly
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-800">카테고리</label>
              <input
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm"
                value={categoryText}
                readOnly
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-800">
                {isSplitPayment ? "총 요청금액" : "원금(금액)"}
              </label>
              <input
                inputMode="numeric"
                className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#f97316]"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={fmtAmount(isSplitPayment ? originalSplitSummary.amount_sum : row.amount)}
                disabled={!editable || saving}
              />
              <div className="mt-1 text-xs text-slate-500">
                {isSplitPayment
                  ? `현재 총 요청금액: ${fmtAmount(originalSplitSummary.amount_sum)}원`
                  : `현재 원금: ${fmtAmount(row.amount)}원`}
              </div>
              {isSplitPayment && (
                <>
                  <div className="mt-1 text-xs text-slate-500">
                    현재 총 결제금액: {fmtAmount(originalSplitSummary.total_sum)}원 (수수료 {fmtAmount(originalSplitSummary.fee_sum)}원 · 부가세 {fmtAmount(originalSplitSummary.vat_sum)}원)
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">분할별 금액 수정</div>
                        <div className="mt-1 text-xs text-slate-500">
                          각 회차 금액을 직접 수정할 수 있습니다. 분할 합계는 총 요청금액과 일치해야 저장됩니다.
                        </div>
                      </div>
                      <div
                        className={[
                          "inline-flex rounded-full px-3 py-1 text-xs font-semibold",
                          splitAmountsValid && splitSumMatchesTotal
                            ? "bg-[#fff7ed] text-[#c2410c] ring-1 ring-[#fed7aa]"
                            : "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
                        ].join(" ")}
                      >
                        분할 합계 {fmtAmount(splitDraftSummary.amount_sum)}원
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {splitItems.map((item, idx) => {
                        const draftAmount = splitAmountNumbers[idx] || 0;
                        const preview = calcPreviewAmounts(draftAmount);
                        return (
                          <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-semibold text-slate-900">분할 {item.split_label}</div>
                              <div className="text-xs text-slate-500">현재 {fmtAmount(item.amount)}원</div>
                            </div>
                            <input
                              inputMode="numeric"
                              className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#f97316]"
                              value={splitAmounts[idx] ?? ""}
                              onChange={(e) => updateSplitAmount(idx, e.target.value)}
                              placeholder={fmtAmount(item.amount)}
                              disabled={!editable || saving}
                            />
                            <div className="mt-2 text-xs text-slate-500">
                              예상 총 결제금액 {fmtAmount(preview.total)}원 (수수료 {fmtAmount(preview.fee)}원 · 부가세 {fmtAmount(preview.vat)}원)
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {!splitAmountsValid && (
                      <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        분할 금액은 모두 0보다 큰 숫자로 입력해 주세요.
                      </div>
                    )}

                    {splitAmountsValid && !splitSumMatchesTotal && (
                      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        분할 합계({fmtAmount(splitDraftSummary.amount_sum)}원)와 총 요청금액({fmtAmount(totalAmountNumber)}원)이 일치해야 저장할 수 있습니다.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {!editable && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                이 결제건은 현재 상태에서 수정할 수 없습니다.
              </div>
            )}

            <div className="mt-2 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                onClick={() => navigate("/pay")}
                disabled={saving}
              >
                목록으로
              </button>

              {canCancel && (
                <button
                  type="button"
                  className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-rose-600 ring-1 ring-rose-300 hover:bg-rose-50"
                  onClick={onCancelRequest}
                  disabled={saving}
                >
                  요청철회
                </button>
              )}

              <button
                type="button"
                className={[
                  "rounded-full px-5 py-2 text-sm font-semibold text-white",
                  editable && !saving ? "bg-[#f97316] hover:bg-[#ea580c]" : "bg-slate-300 cursor-not-allowed",
                ].join(" ")}
                onClick={onSave}
                disabled={!editable || saving}
              >
                {saving ? "저장 중..." : isRejectedLike ? "저장 후 재접수" : "저장"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
